import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Period } from '@/lib/timePeriod';
import type { HubKpisResponse } from '@/types/fraudHub';

/**
 * Fetches the five narrative KPIs that sit at the top of the Fraud Hub.
 *
 * Period-aware (PR 3): the "new in window" count and the top-rule fire
 * count are scoped to the selected period; the open-status counts
 * (Critical / High / Total) deliberately ignore the period — "open" is
 * a status, not a time window.
 *
 * Same staleTime (30 s) as the Critical Board so a hub re-render right
 * after a refetch tick doesn't double-fetch unnecessarily.
 */
export function useFraudHubKpis(
  period: Period,
): UseQueryResult<HubKpisResponse, Error> {
  return useQuery<HubKpisResponse, Error>({
    queryKey: ['fraud-hub', 'kpis', period],
    queryFn: async () => {
      const { data } = await api.get<HubKpisResponse>('/fraud-hub/kpis', {
        params: { period },
      });
      return data;
    },
    staleTime: 30_000,
  });
}
